import { __awaiter } from "tslib";
import { fetchOfficeComponentContext } from '@office-start/component-context-service';
import { fetchIdentity, getOfficeIdentity, } from '@office-start/identity-service';
import { format } from '@office-start/string-formatter-utility';
import { logError } from '@office-start/telemetry-service';
import { AppHostAuthenticationService } from './AppHostAuthenticationService';
import { NestedAppAuthService } from './NestedAppAuthenticationService';
import { AppId, getExpiry, getWamAccessToken, isWindowsAuthAvailable, KnownAuthorities, } from './WindowsTokens';
// Hubsdk will append authId and result to the uri when running the script.
// It will look like 'ms-getoffice:authcallback?appId=aAppId&authId=aAuthId&result=authResult'
const authRedirectUrlFormat = 'ms-getoffice:authcallback?appId={0}';
const getAuthRedirectUrl = (mosAppId) => {
    return new URL(format(authRedirectUrlFormat, mosAppId));
};
const getWamAccessTokenResponseFunction = (user) => {
    const wamAccessTokenResponseFunction = (_tokenConsumer, _forceFetch, resourceUrl) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        try {
            if (!isWindowsAuthAvailable) {
                logError({
                    eventName: 'Access_Token_Response',
                    errorType: 'GetOfficeToken_Error',
                    message: 'Windows auth not available',
                });
                return Promise.reject('Windows auth not available');
            }
            if (!resourceUrl) {
                logError({
                    eventName: 'Access_Token_Response',
                    errorType: 'GetOfficeToken_Error',
                    message: 'Resource not provided',
                });
                return Promise.reject('Resource not provided');
            }
            const result = yield getWamAccessToken(resourceUrl, user);
            const response = (_a = result === null || result === void 0 ? void 0 : result.responseData) === null || _a === void 0 ? void 0 : _a[0];
            const account = response === null || response === void 0 ? void 0 : response.webAccount;
            const isCommercial = (account === null || account === void 0 ? void 0 : account.webAccountProvider.authority) === KnownAuthorities.AAD;
            if (!(response === null || response === void 0 ? void 0 : response.token) ||
                (result === null || result === void 0 ? void 0 : result.responseStatus) !==
                    Windows.Security.Authentication.Web.Core
                        .WebTokenRequestStatus.success) {
                logError({
                    eventName: 'Access_Token_Response',
                    errorType: 'GetOfficeToken_Error',
                    message: 'unable to acquire token from WebTokenRequestResult',
                    debugInfo: ' wamAccessTokenResponseFunction',
                });
                return Promise.reject('unable to acquire token from WebTokenRequestResult');
            }
            return {
                success: true,
                token: response.token,
                format: isCommercial ? 'JWT' : 'RPSCompact',
                source: 'WAM',
                expiryMs: getExpiry(response === null || response === void 0 ? void 0 : response.properties, isCommercial),
                error: result === null || result === void 0 ? void 0 : result.responseError,
                clientId: AppId,
            };
        }
        catch (error) {
            logError({
                eventName: 'Access_Token_Response',
                errorType: 'GetOfficeToken_Error',
                message: `wamAccessTokenResponseFunction failed: ${error}`,
                debugInfo: 'format: JWT, source: WAM',
            });
            return {
                success: false,
                format: 'JWT',
                source: 'WAM',
                token: '',
                error: error,
            };
        }
    });
    return wamAccessTokenResponseFunction;
};
export const getAuthenticationService = (appId) => __awaiter(void 0, void 0, void 0, function* () {
    const officeComponentContext = yield fetchOfficeComponentContext();
    const identityResult = getOfficeIdentity();
    const officeIdentity = identityResult
        ? identityResult
        : yield fetchIdentity();
    if (!officeComponentContext || !officeIdentity) {
        return undefined;
    }
    // TODO: remove testAppActionEnabled check after testing
    const authRedirectUrl = getAuthRedirectUrl(appId);
    const userProfile = getAuthenticationUser(officeIdentity);
    const accessTokenResponseFunction = isWindowsAuthAvailable
        ? getWamAccessTokenResponseFunction(userProfile)
        : officeComponentContext.getAccessToken;
    const appHostAuthenticationService = new AppHostAuthenticationService(accessTokenResponseFunction, userProfile, authRedirectUrl);
    return appHostAuthenticationService;
});
export const getNestedAppAuthenticationService = () => __awaiter(void 0, void 0, void 0, function* () {
    const identityResult = getOfficeIdentity();
    const officeIdentity = identityResult
        ? identityResult
        : yield fetchIdentity();
    if (!officeIdentity) {
        return undefined;
    }
    const userProfile = getAuthenticationUser(officeIdentity);
    const nestedAppAuthService = new NestedAppAuthService(userProfile);
    return Promise.resolve(nestedAppAuthService);
});
const getAuthenticationUser = (officeIdentity) => {
    var _a, _b, _c;
    return {
        profile: {
            upn: (_a = officeIdentity.userPrincipalName) !== null && _a !== void 0 ? _a : '',
            tid: officeIdentity.tenantId,
            oid: officeIdentity.isMsa
                ? getStringForGUID(officeIdentity.objectId)
                : officeIdentity.objectId,
            loginHint: (_b = officeIdentity === null || officeIdentity === void 0 ? void 0 : officeIdentity.loginHint) !== null && _b !== void 0 ? _b : '',
            displayName: (_c = officeIdentity.userDisplayName) !== null && _c !== void 0 ? _c : '',
        },
    };
};
const getStringForGUID = (userId) => {
    if (!userId)
        return userId;
    // Remove all dashes from the user ID
    const processedUserId = userId.replace(/-/g, '');
    // Check if the processed user ID starts with 16 leading zeroes
    if (/^[0]{16}/.test(processedUserId)) {
        // Replace the leading 16 zeroes
        return processedUserId.replace(/^[0]{16}/, '');
    }
    // Return the original user ID if no leading zeroes are found
    return userId;
};
//# sourceMappingURL=getAppHostAuthenticationService.js.map